import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, BarChart3, RotateCcw, Loader2, Plus, Minus, Expand, Play, Pause, PlusSquare } from "lucide-react";
import AddPanel from "./add-panel";
import ConfigPanel from "./config-panel";
import FlowCanvas, { JOURNEY_DROP_ATTR, TRIGGER_DROP_ATTR } from "./flow-canvas";
import { BRANCHING_TYPES, JOURNEY_CATEGORIES, findDefaultTrigger, makeDefaultTriggerChain, makeFlowNode } from "./catalog";
import { useJourneyCatalog } from "./use-catalog";
import {
  changeSendNodeType,
  deleteNode,
  findNode,
  findNodeIdsByLabel,
  findPrecedingAiGenerate,
  insertNode,
  isGroupedCondition,
  linearizeForTestRun,
  serializeFlow,
  setBranchBehavior,
  setWaitThenBehavior,
  updateNodeConfig,
} from "./flow-model";
import { ActionDef, ConditionGroup, FlowNode, InsertionTarget, JourneyCategory, TriggerDef, TriggerInstance } from "./types";
import TestRunModal, { DEFAULT_TEST_RUN_DATA, TestRunData } from "./test-run-modal";
import {
  JourneyChannel,
  JourneyFlowNode,
  useCompileFlow,
  useCreateJourney,
  useCreateJourneyVersion,
  useJourneyFlow,
  useSavedJourney,
  usePublishJourney,
  useTestRunSchema,
  useFlowRun,
} from "./journey-api";
import { chainFromJourneyFlow, chainFromSavedDefinition } from "./journey-flow-import";
import { ExecutionDetail, useExecutionDetail } from "../executions/executions-api";

// "send_text" is the real backend engine's own generic Send node_type —
// confirmed live on dormancy_reactivation/churn_risk_escalation's actual
// stored flows, channel carried in `config.channel` rather than the node
// type itself (unlike this catalog's own send_whatsapp/send_sms/send_email
// ids). Needs to be recognized as a real Send node everywhere a catalog type
// would be, or an imported journey's Send step silently fails every one of
// these checks — content validation, channel collection, and (below) which
// channel to deliver its real template/manual content under.
const SEND_NODE_TYPES = new Set(["send_message", "send_text", "send_email", "send_whatsapp", "send_rcs", "send_sms", "send_push"]);
const CHANNEL_TO_API: Record<string, JourneyChannel> = { WhatsApp: "whatsapp", SMS: "sms", Email: "email" };
// Case-insensitive lookup — a journey imported from GET /v1/bot/journeys/{id}
// stores its channel lowercase ("whatsapp", confirmed live on dormancy_
// reactivation/new_account_welcome), not this UI's capitalized vocabulary
// ("WhatsApp"). A plain CHANNEL_TO_API["whatsapp"] lookup silently misses,
// which was why an imported journey's real channel could drop out of
// collectActiveChannels until the user happened to re-toggle it by hand.
const CHANNEL_TO_API_CI: Record<string, JourneyChannel> = Object.fromEntries(
  Object.entries(CHANNEL_TO_API).map(([k, v]) => [k.toLowerCase(), v])
);
const mapChannelLabel = (ch?: string): JourneyChannel | undefined => (ch ? CHANNEL_TO_API_CI[ch.toLowerCase()] : undefined);
const SEND_TYPE_TO_CHANNEL: Record<string, JourneyChannel> = { send_whatsapp: "whatsapp", send_sms: "sms", send_email: "email" };

// Which channel a Send node actually targets — from its own specific catalog
// type when it has one, or (a real imported "send_text"/this UI's generic
// "send_message" card) from its own `config.channel` field otherwise. Used
// everywhere a Send node's real content needs to be read under the right
// field for its *actual* channel, not guessed from a type string that may
// just be the engine's generic one.
function effectiveSendChannel(node: FlowNode): JourneyChannel | undefined {
  return SEND_TYPE_TO_CHANNEL[node.type] || mapChannelLabel(node.config?.channel);
}

// A Send node's own typed-in message body lives under `messageBody` for
// SMS/Push, `description` for everything else (WhatsApp/Email/RCS/generic) —
// see config-panel.tsx's MessagePreviewEditor (`isSms || isPush ? messageBody
// : description`). Dispatches on the *effective* channel (see above), not the
// literal node.type, so a generic/imported SMS-channeled node still reads
// its real content from the right field instead of silently checking the
// wrong one and reporting it as empty.
function getManualBodyText(node: FlowNode): string {
  const channel = effectiveSendChannel(node);
  const field = node.type === "send_push" || channel === "sms" ? "messageBody" : "description";
  return node.config?.[field] || "";
}

// Nodes the Test Run walkthrough can't actually run yet: a Send step with no
// content at all (no template picked, no AI-generated prompt feeding it, and
// nothing manually typed into its own body either — any one of those three
// is enough) or a Condition with nothing to check.
// AI Moderate steps whose FALSE ("refused") side reaches no Send card. When
// moderation refuses, that path silently sends nothing (backend, 27 Oct) — the
// prebuilt salary journey puts a plain pre-approved message there instead.
// A warning, not a blocker: ending on refusal can be deliberate.
function hasSendDownstream(node: FlowNode | null): boolean {
  if (!node) return false;
  if (SEND_NODE_TYPES.has(node.type)) return true;
  if (node.branches) return hasSendDownstream(node.branches.true.next) || hasSendDownstream(node.branches.false.next);
  if (node.optionBranches) return node.optionBranches.some((b) => hasSendDownstream(b.next));
  return hasSendDownstream(node.next);
}

function findSilentModerationRefusals(root: FlowNode | null): FlowNode[] {
  const out: FlowNode[] = [];
  const walk = (node: FlowNode | null) => {
    if (!node) return;
    if (node.type === "ai_moderate" && node.branches && !hasSendDownstream(node.branches.false.next)) out.push(node);
    if (node.branches) {
      walk(node.branches.true.next);
      walk(node.branches.false.next);
    } else if (node.optionBranches) node.optionBranches.forEach((b) => walk(b.next));
    else walk(node.next);
  };
  walk(root);
  return out;
}

function computeInvalidNodeIds(root: FlowNode | null): Set<string> {
  const invalid = new Set<string>();
  const walk = (node: FlowNode | null, precedingNode?: FlowNode) => {
    if (!node) return;
    if (SEND_NODE_TYPES.has(node.type)) {
      const hasTemplate = !!node.config?.template?.trim();
      const hasManualText = !!getManualBodyText(node).trim();
      const hasAiPrompt = precedingNode?.type === "ai_generate" && !!precedingNode.config?.prompt?.trim();
      const hasContent = hasTemplate || hasManualText || hasAiPrompt;
      if (!hasContent) invalid.add(node.id);
    }
    if (node.type === "condition") {
      const c = node.config || {};
      const ok = isGroupedCondition(c)
        ? (c.conditionGroups as ConditionGroup[]).some((g) => g.rows.some((r) => r.attribute && r.value !== ""))
        : c.mode === "boolean"
          ? !!c.booleanCheck
          : !!c.conditions?.[0]?.attribute && c.conditions?.[0]?.value !== "";
      if (!ok) invalid.add(node.id);
    }
    if (node.branches) {
      walk(node.branches.true.next, node);
      walk(node.branches.false.next, node);
    } else if (node.optionBranches) {
      node.optionBranches.forEach((b) => walk(b.next, node));
    } else {
      walk(node.next, node);
    }
  };
  walk(root, undefined);
  return invalid;
}

// Channels backed by a real Send<Channel> card actually present in the tree
// (not an AI Generate node's own multi-select — that's just which content
// variants to generate; an AI Generate node never dispatches anything by
// itself, see handleTestRunSubmit's applyGenerated, which only ever patches
// a *following* Send node's config).
function collectDirectSendChannels(root: FlowNode | null): Set<JourneyChannel> {
  const channels = new Set<JourneyChannel>();
  const walk = (node: FlowNode | null) => {
    if (!node) return;
    const direct = SEND_TYPE_TO_CHANNEL[node.type];
    if (direct) {
      channels.add(direct);
    } else if (node.config?.channel) {
      // Catches both this builder's own "send_message" generic card AND a
      // real imported journey's Send node, which comes back as the backend
      // engine's own generic node_type ("send_text" — confirmed live on
      // dormancy_reactivation/new_account_welcome) rather than this catalog's
      // send_whatsapp/send_sms/send_email ids, so SEND_TYPE_TO_CHANNEL alone
      // never matches it. Without this fallback, an imported journey's Send
      // node contributed nothing here at all, leaving the AI Generate node's
      // own (differently-cased) channel selection as the only signal.
      const mapped = mapChannelLabel(node.config.channel);
      if (mapped) channels.add(mapped);
    }
    if (node.branches) {
      walk(node.branches.true.next);
      walk(node.branches.false.next);
    } else if (node.optionBranches) {
      node.optionBranches.forEach((b) => walk(b.next));
    } else {
      walk(node.next);
    }
  };
  walk(root);
  return channels;
}

// Which channels this journey actually uses — from every AI Generate node's
// own channel multi-select plus every direct Send<Channel> node's type —
// walking the whole tree (including both sides of every branch), not just
// the currently-selected node. Drives which contact fields the Test Run
// modal shows (see test-run-modal.tsx): GET /v1/bot/test-run/schema doesn't
// vary by channel at all (confirmed live — a `channels=` query param has no
// effect), so this has to be worked out client-side from the built journey.
function collectActiveChannels(root: FlowNode | null): Set<JourneyChannel> {
  const channels = collectDirectSendChannels(root);
  const walk = (node: FlowNode | null) => {
    if (!node) return;
    if (node.type === "ai_generate" && Array.isArray(node.config?.channels)) {
      (node.config.channels as string[]).forEach((ch) => {
        const mapped = mapChannelLabel(ch);
        if (mapped) channels.add(mapped);
      });
    }
    if (node.branches) {
      walk(node.branches.true.next);
      walk(node.branches.false.next);
    } else if (node.optionBranches) {
      node.optionBranches.forEach((b) => walk(b.next));
    } else {
      walk(node.next);
    }
  };
  walk(root);
  return channels;
}

// Deleting a Send<Channel> card used to leave that channel "active" forever
// if an AI Generate node upstream still had it ticked in its own channel
// multi-select — collectActiveChannels trusted that selection on its own, so
// e.g. removing "Send SMS" from the canvas didn't stop "sms" from still
// showing up in the next Test Run's channels payload. Re-validates every AI
// Generate node's channel list against collectDirectSendChannels right after
// a delete, dropping any channel that no longer has a real Send node behind
// it.
function pruneStaleAiChannels(root: FlowNode | null): FlowNode | null {
  const liveChannels = collectDirectSendChannels(root);
  const walk = (node: FlowNode | null): FlowNode | null => {
    if (!node) return null;
    let current = node;
    if (node.type === "ai_generate" && Array.isArray(node.config?.channels)) {
      const filtered = (node.config.channels as string[]).filter((ch) => {
        const mapped = mapChannelLabel(ch);
        return !mapped || liveChannels.has(mapped);
      });
      if (filtered.length !== node.config.channels.length) {
        current = { ...node, config: { ...node.config, channels: filtered } };
      }
    }
    if (current.branches) {
      return {
        ...current,
        branches: {
          true: { ...current.branches.true, next: walk(current.branches.true.next) },
          false: { ...current.branches.false, next: walk(current.branches.false.next) },
        },
      };
    }
    if (current.optionBranches) {
      return { ...current, optionBranches: current.optionBranches.map((b) => ({ ...b, next: walk(b.next) })) };
    }
    return { ...current, next: walk(current.next) };
  };
  return walk(root);
}

// New, self-contained "Customer Journey" builder — vertical canvas, backed by
// the real trigger/action catalog from GET /v1/bot/triggers and
// GET /v1/bot/actions (see use-catalog.ts), optionally scoped to one journey
// template's own blocks when opened from the modal's "start from a template"
// list. Deliberately doesn't touch ../create-builder (the existing
// WhatsApp/RCS builder, which keeps its own real save/publish path); nothing
// here persists to a backend yet.

const FALLBACK_TRIGGER: TriggerInstance = { id: "", label: "Trigger", categoryId: "customer", config: {} };

// Turns a finished Test Run's execution (GET /v1/executions/{workflow_id})
// into what actually happened. The diagnostics (outcome, outcome_reason,
// send_failures [{step_id, reason}], branch_dead_ends) are top-level on the
// execution now; older runs kept them in submitted_data, read as a fallback.
// Each failure `reason` is written to be shown to the operator verbatim
// (e.g. "SMS template '…' has no content for this tenant — create it in the
// portal first"). Never says "sent" unless something was dispatched.
const failureReason = (f: any): string => (typeof f === "string" ? f : f?.reason || f?.error || f?.detail || f?.step || "unknown error");
// A send refused at send time for consent (opt-out, DND, do_not_contact,
// marketing_opt_in: false) — a normal, reportable outcome, not an error.
const isConsentRefusal = (f: any) => /^suppressed:consent/i.test(failureReason(f));

const RUN_POLL_SECONDS = 30;

function describeRunOutcome(detail: ExecutionDetail | null, finished: boolean, workflowId: string): { kind: "success" | "error" | "warning" | "info"; title: string; description?: string } {
  if (!detail)
    return {
      kind: "warning",
      title: "Couldn't read this test run's result",
      description: `The run started, but GET /v1/executions/${workflowId} never returned it. Check the Executions tab, or send this workflow id to the backend.`,
    };
  if (!finished)
    return {
      kind: "info",
      title: `Test run hasn't finished after ${RUN_POLL_SECONDS}s`,
      description: `Status: ${detail.status || "unknown"}${detail.current_step ? ` at ${detail.current_step}` : ""} · workflow ${workflowId}`,
    };
  const top = detail as any;
  const d = detail.submitted_data || {};
  const reason: string = top.outcome_reason || d.outcome_reason || top.outcome || detail.status || "unknown";
  const sent = Number(top.messages_dispatched ?? d.messages_dispatched ?? top.emails_dispatched ?? d.emails_dispatched) || 0;
  const allFailures: any[] = Array.isArray(top.send_failures) ? top.send_failures : Array.isArray(d.send_failures) ? d.send_failures : [];
  const consent = allFailures.filter(isConsentRefusal);
  const failures = allFailures.filter((f) => !isConsentRefusal(f));
  const deadEnds: any[] = Array.isArray(top.branch_dead_ends) ? top.branch_dead_ends : Array.isArray(d.branch_dead_ends) ? d.branch_dead_ends : [];
  const steps = Array.isArray(d.steps_completed) ? d.steps_completed.length : detail.steps_completed?.length;
  const notes = [
    steps != null ? `${steps} step${steps === 1 ? "" : "s"} ran` : "",
    consent.length ? `Not sent for consent: ${Array.from(new Set(consent.map(failureReason))).join("; ")}` : "",
    deadEnds.length ? `Branch dead end${deadEnds.length === 1 ? "" : "s"}: ${deadEnds.map((x) => (typeof x === "string" ? x : x?.reason || x?.step_id || JSON.stringify(x))).join("; ")}` : "",
  ].filter(Boolean);
  const stepsText = notes.length ? notes.join(" · ") : undefined;

  if (failures.length > 0) {
    const reasons = Array.from(new Set(failures.map(failureReason))).join("; ");
    return { kind: "error", title: `${failures.length} of ${sent + allFailures.length} send${sent + allFailures.length === 1 ? "" : "s"} failed: ${reasons}`, description: stepsText };
  }
  if (reason === "dry_run_no_dispatch") return { kind: "info", title: "Dry run finished — nothing sent", description: `${stepsText ? `${stepsText}. ` : ""}Tick "Actually send this test" to deliver.` };
  if (sent > 0) return { kind: "success", title: `${sent} message${sent === 1 ? "" : "s"} sent`, description: stepsText };
  if (consent.length > 0) return { kind: "info", title: "Nothing sent — the contact hasn't consented", description: stepsText };
  if (detail.status === "failed") return { kind: "error", title: "Test run failed", description: reason };
  return { kind: "warning", title: "Journey finished without sending anything", description: `${reason}${stepsText ? ` · ${stepsText}` : ""}` };
}

const CustomerJourneyBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: {
      trigger?: { categoryId: string; triggerId: string };
      journeyId?: string;
      journeyName?: string;
      // Set when opened from "All Bots" to edit an already-created bot — a
      // real per-tenant numeric id (GET /journeys/{id}), distinct from
      // `journeyId` above which is the public prebuilt-template catalog id
      // (GET /v1/bot/journeys/{id}).
      savedJourneyId?: number | string;
    };
  };
  const journeyId = location.state?.journeyId;
  const savedJourneyId = location.state?.savedJourneyId;

  // Hoisted above useJourneyCatalog (declared again, unchanged, further down
  // for the rest of the component) purely so the currently-selected node's
  // channels — when it's an AI Generate node — can be passed into the catalog
  // hook below, which re-fetches GET /v1/bot/actions?channels=... whenever
  // that selection changes so the Add panel only shows Send cards for the
  // channels currently picked on that node.
  const [chainRoot, setChainRoot] = useState<FlowNode | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNode = selectedNodeId ? findNode(chainRoot, selectedNodeId) : null;
  const aiGenerateChannels = selectedNode?.type === "ai_generate" ? (selectedNode.config?.channels as string[] | undefined) : undefined;

  // Scoping to a journey (picked from the "Create New Bot Journey" modal's
  // template list) narrows GET /v1/bot/triggers and /v1/bot/actions down to
  // just that journey's own blocks via ?journey=<id> — see use-catalog.ts.
  const { loading: catalogLoading, loadError: catalogLoadError, journeyTemplates } = useJourneyCatalog(journeyId, aiGenerateChannels);

  // The real prebuilt canvas for this journey (GET /v1/bot/journeys/{id}) —
  // only fetched when opened from a template; `data` stays null for
  // "start from scratch" and for a journey id the backend doesn't recognize,
  // in which case the seeding effect below falls back to the same default
  // chain it always used.
  const { data: journeyFlow, loading: journeyFlowLoading } = useJourneyFlow(journeyId);

  // The real already-saved bot (GET /journeys/{id}) when opened from "All
  // Bots" rather than a template — see chainFromSavedDefinition for how its
  // `definition` (a generic, backend-untyped object) gets turned back into a
  // canvas.
  const { data: savedJourney, loading: savedJourneyLoading } = useSavedJourney(savedJourneyId);

  // Resolved once the catalog is loaded — JOURNEY_CATEGORIES is empty before
  // that, so this falls back to a placeholder rather than crashing on a
  // lookup that can't succeed yet (see the seeding effect below, which fires
  // once loading flips false).
  //
  // Per direct ask: a genuinely-from-scratch journey (no template, no saved
  // bot, nothing passed in `location.state`) now starts with NO trigger
  // picked at all — the operator chooses one explicitly from the real
  // backend catalog (see TriggerConfig's picker) rather than this silently
  // defaulting to "whichever trigger happens to be usable first." Opening a
  // template or an existing saved bot still auto-resolves as before — their
  // real trigger always comes from the real flow anyway (see the seeding
  // effect below), this memo is just their *placeholder until that loads*.
  const initialTrigger = useMemo<TriggerInstance>(() => {
    if (catalogLoading) return FALLBACK_TRIGGER;
    const passed = location.state?.trigger;
    const category = passed ? JOURNEY_CATEGORIES.find((c) => c.id === passed.categoryId) : null;
    const triggerDef = passed ? category?.triggers.find((t) => t.id === passed.triggerId) : undefined;
    if (triggerDef && category) return { id: triggerDef.id, label: triggerDef.label, categoryId: category.id, config: {} };

    const isFromScratch = !journeyId && !savedJourneyId && !passed;
    if (isFromScratch) return FALLBACK_TRIGGER;

    const fallback = findDefaultTrigger();
    if (!fallback) return FALLBACK_TRIGGER;
    return { id: fallback.trigger.id, label: fallback.trigger.label, categoryId: fallback.category.id, config: {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, catalogLoading]);

  const [botName, setBotName] = useState(location.state?.journeyName || savedJourney?.name || "Add a bot name");
  const [editingName, setEditingName] = useState(false);
  const [trigger, setTrigger] = useState<TriggerInstance>(initialTrigger);
  // Hoisted above the seeding effect below (which needs to set this when
  // opening an already-saved bot for editing) — declared again nowhere else.
  const [publishedJourney, setPublishedJourney] = useState<{ id: number } | null>(null);
  // State (not a ref) on purpose: the page-level loading gate below reads
  // this to know "the one-time seed has happened" as distinct from
  // "chainRoot currently has a node in it" — chainRoot legitimately goes
  // back to null after the seed, e.g. deleting the only action (Fetch
  // Profile) empties the canvas on purpose (see handleDeleteNode/deleteNode)
  // and must render the empty canvas, not re-show the loading spinner.
  const [seeded, setSeeded] = useState(false);

  // Per direct ask: warn before leaving with unsaved work. Flips true on any
  // real edit to trigger/chainRoot/botName — "real" meaning after the
  // one-time seed above has already landed, not the seed's own initial
  // setTrigger/setChainRoot/setBotName calls (seededOnceRef below skips
  // exactly that first post-seed render so opening a fresh/saved journey
  // doesn't immediately show as "changed"). Cleared by handleSaveDraft,
  // handleTestRunSubmit's draft creation and handlePublish — anything that
  // actually persists the current canvas server-side.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const seededOnceRef = useRef(false);

  // Seed the trigger + chain exactly once, as soon as the catalog AND (when
  // opened from a template) that journey's real flow are both ready. A real
  // flow converts via chainFromJourneyFlow into the exact prebuilt canvas
  // (e.g. loan_abandonment_recovery's 24h/48h/24h reminder cadence); no flow
  // (scratch, or a journey id the backend doesn't recognize) falls back to
  // the same default Fetch Profile → AI Generate chain as before.
  useEffect(() => {
    if (catalogLoading || journeyFlowLoading || (!!savedJourneyId && savedJourneyLoading) || seeded) return;
    setSeeded(true);

    const savedCanvasNodes: JourneyFlowNode[] | undefined = Array.isArray(savedJourney?.definition?._canvas?.nodes)
      ? savedJourney!.definition!._canvas.nodes
      : undefined;
    // The real trigger node's own config (priority/timeout_ms, e.g.
    // fraud_anomaly_alert's "highest"/1000ms — shows up as the "High
    // priority"/"Immediate {< 1 sec}" badges next to Bot Starting Point,
    // see flow-canvas.tsx) merges in here — which trigger this is stays on
    // the existing scoped-catalog resolution above, only its config comes
    // from the real flow when there is one. An opened-for-editing saved bot
    // takes priority over a template; its own canvas (when present) carries
    // the same real trigger-node config shape a template's does, and the
    // flat-fallback case (no real canvas saved) at least has the raw
    // trigger_config from GET /journeys/{id}'s own definition.
    const savedCanvasTriggerNode = savedCanvasNodes?.find((n) => n.type === "trigger");
    const realTriggerNode = journeyFlow?.flow.nodes.find((n) => n.type === "trigger");
    const realTriggerConfig = savedCanvasTriggerNode?.config || (savedJourney ? savedJourney.definition?.trigger_config : undefined) || realTriggerNode?.config;
    // The scoped trigger catalog can come back empty for a template whose
    // events were renamed (dormancy_reactivation, churn_risk_escalation,
    // fraud_anomaly_alert: GET /v1/bot/triggers?journey=… → count 0, while
    // their flow's trigger node uses e.g. customer_dormancy_flagged). An
    // empty id makes FlowCanvas hide the whole chain behind the "pick a
    // trigger" box, so fall back to the flow's own trigger event.
    const flowTriggerNode = savedCanvasTriggerNode || realTriggerNode;
    const flowEvent: string | undefined = flowTriggerNode?.config?.event || journeyFlow?.trigger_event || savedJourney?.trigger || undefined;
    const baseTrigger: TriggerInstance =
      initialTrigger.id === "" && flowEvent && (journeyId || savedJourneyId)
        ? {
            id: flowEvent,
            label: flowTriggerNode?.label || flowEvent,
            categoryId: JOURNEY_CATEGORIES.find((c) => c.triggers.some((t) => t.id === flowEvent))?.id || initialTrigger.categoryId,
            config: {},
          }
        : initialTrigger;
    setTrigger(
      realTriggerConfig
        ? {
            ...baseTrigger,
            // Every real trigger node carries its own literal `label` (e.g.
            // salary_day_loan's "Salary Credited", new_account_welcome's
            // "Account Opened" — confirmed live on every real journey) — a
            // far more reliable source than `initialTrigger.label`, which for
            // a fresh template pick falls back to findDefaultTrigger()
            // reading the shared, module-level JOURNEY_CATEGORIES catalog
            // (catalog.ts). That global gets overwritten by whichever journey
            // scope was fetched *last*, and a scope already cached this
            // session (use-catalog.ts's loadedScopes) skips refetching
            // entirely — so opening journey B after A (both already visited
            // this session) could silently keep showing A's first trigger
            // name as B's. Preferring the real node's own label sidesteps
            // that global entirely; savedJourney?.trigger (a raw event
            // string, not a pretty label) stays as the last-resort fallback
            // for an old saved bot with no real canvas/flow to read from.
            label: savedCanvasTriggerNode?.label || realTriggerNode?.label || savedJourney?.trigger || baseTrigger.label,
            config: {
              ...realTriggerConfig,
              // The "Execution timeout" dropdown's own option strings (see
              // TriggerConfig in config-panel.tsx) — derived from the real
              // `timeout_ms` so both that dropdown and the "Immediate {< 1
              // sec}" badge next to Bot Starting Point (flow-canvas.tsx)
              // reflect it, not just a bare number.
              timeout:
                realTriggerConfig.timeout_ms != null
                  ? realTriggerConfig.timeout_ms <= 1000
                    ? "Execute immediately { < 1 second }"
                    : realTriggerConfig.timeout
                  : realTriggerConfig.timeout,
            },
          }
        : baseTrigger
    );

    if (savedJourney) {
      setBotName(savedJourney.name);
      setPublishedJourney({ id: savedJourney.id });
      setChainRoot(chainFromSavedDefinition(savedJourney.definition) || makeDefaultTriggerChain());
    } else {
      const imported = journeyFlow ? chainFromJourneyFlow(journeyFlow) : null;
      setChainRoot(imported || makeDefaultTriggerChain());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLoading, journeyFlowLoading, savedJourneyLoading]);

  // Marks real edits dirty — skips the one render right after seeding sets
  // trigger/chainRoot/botName for the first time, so opening a fresh or
  // already-saved journey never starts out flagged as "changed".
  useEffect(() => {
    if (!seeded) return;
    if (!seededOnceRef.current) {
      seededOnceRef.current = true;
      return;
    }
    setHasUnsavedChanges(true);
  }, [seeded, trigger, chainRoot, botName]);

  // Native browser navigation (back/forward, tab close, refresh, typing a new
  // URL) can't be intercepted with a custom dialog from inside a plain
  // <BrowserRouter> (no data router here, so react-router's own useBlocker/
  // usePrompt aren't available) — this is the standard fallback: returning a
  // string makes the browser show its own "Leave site?" confirmation. Its
  // wording can't be customized by any modern browser (a long-standing
  // anti-abuse restriction), unlike the in-app "<-"/"All Bots" guard below,
  // which uses this app's own dialog instead since those clicks are fully
  // within our control.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const [insertionTarget, setInsertionTarget] = useState<InsertionTarget | null>(null);
  // Live state for a palette item currently being press-dragged (see
  // add-panel.tsx) — `dragPayload` drives the floating ghost badge that
  // follows the pointer, `dragHoverTarget` is whichever "+" slot the pointer
  // is currently over (resolved by hit-testing the DOM), fed to FlowCanvas
  // purely for the hover highlight.
  const [dragPayload, setDragPayload] = useState<{ action: ActionDef; x: number; y: number } | null>(null);
  const [dragHoverTarget, setDragHoverTarget] = useState<InsertionTarget | null>(null);

  // Same idea as the action palette's drag state above, but for picking a
  // trigger from the new catalog picker (TriggerConfig, config-panel.tsx) —
  // only relevant while no trigger is set yet (trigger.id === ""). Kept
  // entirely separate from dragPayload/dragHoverTarget above rather than
  // unifying them: a trigger drop target is one fixed element (the canvas's
  // own trigger box), not a tree of "+" slots, so it doesn't need the same
  // InsertionTarget/JOURNEY_DROP_ATTR machinery at all.
  const [triggerDragPayload, setTriggerDragPayload] = useState<{ trigger: TriggerDef; category: JourneyCategory; x: number; y: number } | null>(null);
  const [triggerDragActive, setTriggerDragActive] = useState(false);

  // Resolves through any AI Moderate/Format/Summarize steps in between (e.g.
  // Dormancy Reactivation's gen -> mod -> send) to the real AI Generate node
  // feeding this one, not just whatever's directly before it — see
  // findPrecedingAiGenerate's own comment for why that one hop wasn't enough.
  const precedingNode = selectedNodeId ? findPrecedingAiGenerate(chainRoot, selectedNodeId) : undefined;
  const precedingType = precedingNode?.type;

  // ── Canvas zoom / fullscreen ─────────────────────────────────────────────
  const [canvasScale, setCanvasScale] = useState(1);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) canvasWrapRef.current?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
  };
  // A wide prebuilt template (many branches) overflows the canvas, and a
  // scroll container always opens at scrollLeft 0 — which pushes the trigger
  // off to the right. Start scrolled to the middle so the trigger sits at the
  // top centre; bumped again by Reset Changes.
  const [centerCanvasTick, setCenterCanvasTick] = useState(0);
  useEffect(() => {
    if (!seeded) return;
    const frame = requestAnimationFrame(() => {
      const el = canvasWrapRef.current;
      if (el) el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    });
    return () => cancelAnimationFrame(frame);
  }, [seeded, centerCanvasTick]);

  // ── Test Run — validates required content, collects a sample customer,
  // then walks the chain highlighting one node at a time. Nothing here calls
  // a real backend; it's a visual walkthrough of the journey as configured. ──
  const [invalidNodeIds, setInvalidNodeIds] = useState<Set<string>>(new Set());
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [testRunData, setTestRunData] = useState<TestRunData>(DEFAULT_TEST_RUN_DATA);
  // Opened via "All Bots" rather than a template (see index.tsx), only
  // `savedJourneyId` is ever passed — the template's own `journeyId` is gone
  // for good (createJourney never sends it, so GET /journeys/{id} has nowhere
  // to return it from). Recovered here by matching this saved bot's own
  // `trigger` back against the public catalog's `trigger_event` for exactly
  // this reason — without it, test-run/schema?journey= falls back to the
  // bare Customer-details-only shape for every re-opened bot, regardless of
  // which journey it actually is.
  const effectiveJourneyId =
    journeyId || journeyTemplates.find((t) => t.triggerEvent && savedJourney && t.triggerEvent === savedJourney.trigger)?.id;

  // Real GET /v1/bot/test-run/schema?journey= — the modal's fields are
  // whatever this journey's own schema defines (see test-run-modal.tsx),
  // not a fixed Customer/Salary/Loan set every journey used to get.
  const { data: testRunSchema, loading: testRunSchemaLoading } = useTestRunSchema(effectiveJourneyId);
  // Which channels the built journey actually uses right now — recomputed
  // whenever the chain changes, so adding/removing a Send Email node (or
  // flipping an AI Generate node's channel multi-select) updates which
  // contact fields the modal shows the next time it's opened.
  const activeChannels = useMemo(() => collectActiveChannels(chainRoot), [chainRoot]);
  const [runSteps, setRunSteps] = useState<FlowNode[]>([]);
  const [runIndex, setRunIndex] = useState(-1);
  const [runPaused, setRunPaused] = useState(false);
  const [triggerCompleted, setTriggerCompleted] = useState(false);
  const [completedNodeIds, setCompletedNodeIds] = useState<Set<string>>(new Set());
  const isRunning = runIndex >= 0 && runIndex < runSteps.length;
  const runningNodeId = isRunning ? runSteps[runIndex].id : null;

  useEffect(() => {
    if (!isRunning || runPaused) return;
    const current = runSteps[runIndex];
    const t = setTimeout(() => {
      setCompletedNodeIds((prev) => new Set(prev).add(current.id));
      if (runIndex + 1 < runSteps.length) {
        setRunIndex((i) => i + 1);
      } else {
        setRunIndex(-1);
        toast.success(
          BRANCHING_TYPES.has(current.type)
            ? "Test run reached a condition — pick CLICKED or NOT CLICKED on the canvas to keep testing that path."
            : "Test run complete."
        );
      }
    }, 1400);
    return () => clearTimeout(t);
  }, [isRunning, runPaused, runIndex, runSteps]);

  const handleTestRunClick = () => {
    const invalid = computeInvalidNodeIds(chainRoot);
    if (invalid.size > 0) {
      setInvalidNodeIds(invalid);
      toast.error("Please add a message first to test");
      return;
    }
    const silentRefusals = findSilentModerationRefusals(chainRoot);
    setInvalidNodeIds(new Set(silentRefusals.map((n) => n.id)));
    if (silentRefusals.length > 0) {
      toast.warning(`"${silentRefusals[0].label}" sends nothing if moderation refuses`, {
        description: "Add a Send card with a pre-approved template on its FALSE branch, or the journey goes quiet whenever content is refused.",
      });
    }
    setTestRunOpen(true);
  };

  const { runFlow } = useFlowRun();
  const { get: getExecution } = useExecutionDetail();

  // /flows/run only says the workflow started. The real outcome lands in
  // GET /v1/executions/{workflow_id}'s submitted_data once it finishes (~5s
  // with fast_forward) — polled here so the toast reports what actually
  // happened, not what the checkbox was set to.
  // Returns the last execution it managed to read, finished or not — so the
  // toast can tell "couldn't read the result" (null: 404 / wrong id / no
  // tenant) apart from "read it, but it hasn't finished yet".
  const waitForRunOutcome = async (workflowId: string): Promise<{ detail: ExecutionDetail | null; finished: boolean }> => {
    let last: ExecutionDetail | null = null;
    for (let i = 0; i < RUN_POLL_SECONDS; i++) {
      await new Promise((r) => setTimeout(r, 1000));
      const detail = await getExecution(encodeURIComponent(workflowId));
      if (detail) last = detail;
      if ((detail as any)?.outcome_reason || detail?.submitted_data?.outcome_reason || detail?.status === "completed" || detail?.status === "failed") return { detail, finished: true };
    }
    return { detail: last, finished: false };
  };
  // Publish gate: POST /v1/bot/flows/compile's own `blockers` (real,
  // engine-level checks — e.g. "Send Push isn't implemented yet") is the
  // single source of truth for whether Publish unlocks. There used to be a
  // second, separate gate here (POST /v1/bot/test-run's test_run_passed) run
  // alongside this one — two independent checks that could disagree, with no
  // rule for which one won. Dropped in favor of just this one round trip;
  // `compiledDefinition` (this call's own `journey_definition` output — the
  // engine-ready step list Publish actually saves, not the canvas's own
  // {startNodeId,nodes,edges} graph) doubles as "has a Test Run actually run
  // since the last edit", same role the old testRunPassed flag had. Cleared
  // by handleChangeNode/handleDeleteNode/insertActionAt below (an actual
  // edit to the chain) — not by every setChainRoot, since this same Test Run
  // also writes real generated content back into the chain and that
  // shouldn't invalidate the pass it just earned.
  const { compile } = useCompileFlow();
  const [compileBlockers, setCompileBlockers] = useState<string[] | null>(null);
  const [compiledDefinition, setCompiledDefinition] = useState<Record<string, any> | null>(null);
  const canPublish = !!compiledDefinition && (compileBlockers?.length ?? 0) === 0;
  // Once this bot has a real saved journey id (first successful Publish —
  // POST /journeys, confirmed live), re-publishing after further edits goes
  // through POST /journeys/{id}/versions instead of creating a second journey.
  const { create: createJourney } = useCreateJourney();
  const { createVersion: createJourneyVersion } = useCreateJourneyVersion();
  const { publish } = usePublishJourney();
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // A real edit to the chain invalidates everything the last Test Run
  // proved — the compiled definition especially, since publishing it after
  // further changes would ship something the operator never actually tested.
  const invalidateTestRun = () => {
    setCompiledDefinition(null);
    setCompileBlockers(null);
  };

  const handleTestRunSubmit = async (data: TestRunData) => {
    const steps = chainRoot ? linearizeForTestRun(chainRoot) : [];
    setTestRunData(data);
    setTestRunOpen(false);
    if (steps.length === 0) {
      toast.info("Nothing to run yet — add a step to the journey first.");
      return;
    }

    // Publish gate — POST /v1/bot/flows/compile's own `blockers` (real,
    // engine-level checks — e.g. "Send Push isn't implemented yet") is the
    // only check now; the separate POST /v1/bot/test-run gate used to run
    // alongside it, and the two could disagree with no rule for which one
    // won, so that second round trip is gone. `contact_id` must be a
    // *present* key (confirmed live: omitting it entirely 422s with "Field
    // required"), but an empty string is genuinely fine (confirmed live:
    // still compiles normally) — the Test Run modal no longer always
    // collects mobile_number (hidden when the journey has no WhatsApp/SMS
    // step), so this can't assume it was typed in; `|| ""` keeps the key
    // present either way.
    const flow = serializeFlow(trigger, chainRoot);
    const contactId = data.values.mobile_number || "";
    const { result: compileResult, error: compileError } = await compile(flow, contactId);
    if (compileResult) {
      setCompiledDefinition(compileResult.journey_definition);
      setCompileBlockers(compileResult.blockers);
      // Blockers name the offending card by its own label (e.g. "'Welcome'
      // has no channel picked — ...", "'AI Personalise variables' has no
      // prompt template. ...") — turn that into a real canvas highlight via
      // the same invalidNodeIds mechanism the client-side checks already
      // use, instead of only ever surfacing as a toast with no pointer to
      // which card actually caused it.
      const blockedIds = new Set<string>();
      compileResult.blockers.forEach((reason) => {
        const match = reason.match(/^'([^']+)'/);
        if (match) findNodeIdsByLabel(chainRoot, match[1]).forEach((id) => blockedIds.add(id));
      });
      if (blockedIds.size > 0) setInvalidNodeIds(blockedIds);
    } else {
      setCompiledDefinition(null);
      setCompileBlockers([compileError || "Couldn't compile this flow."]);
    }

    if (compileResult && compileResult.blockers.length === 0) {
      toast.success("Test run passed — Publish Bot is now enabled.");
    } else if (compileResult && compileResult.blockers.length > 0) {
      toast.error(compileResult.blockers[0]);
      compileResult.blockers.slice(1).forEach((reason) => toast.error(reason));
    } else if (compileError) {
      toast.error(compileError);
    }

    // Real execution — confirmed by backend (Joseph Dalm) against prod with
    // his own account's token: POST /journeys to get a draft journey_id
    // (reusing one already created by an earlier Test Run or Publish on this
    // bot — same create-vs-reuse branch handlePublish uses below, so this
    // never creates a second journey record for the same bot), *skip*
    // /journeys/{id}/publish entirely for a test run, then POST
    // /v1/bot/flows/run {flow, journey_id, contact_id, tenant_id, data:
    // <the modal's own field values>} directly against that draft. `data` is
    // what every real Condition node reads, so this is the path that
    // actually exercises branch logic and shows up in the tenant's
    // Executions tab and in Temporal (confirmed: kyc_completion_status:
    // "Completed" correctly skipped the reminder and sent the deposit
    // incentive, not just "some message went out"). `flow` is always this
    // call's own current canvas state, so the stored draft doesn't need a
    // fresh version pushed on every single Test Run. Replaces the earlier
    // (incorrect) attempt at this fix, which called a since-corrected
    // /v1/bot/flows/publish endpoint — and, before that, directly called
    // /v1/bot/test-run/send, which only ever did fetch-profile -> ai-generate
    // -> deliver with no Condition/branch logic at all, so every Condition
    // node was silently ignored and nothing ever appeared in
    // Executions/Temporal.
    //
    // `dry_run` (live on prod — confirmed by backend 27 Oct: false → all
    // steps + emails sent; true → all steps, 0 dispatched, outcome_reason
    // dry_run_no_dispatch): the whole journey still
    // runs for real either way — conditions, branches, AI, the execution row
    // — only the actual sends are skipped when true. "Actually send this
    // test" unchecked means dry_run: true (full real trace, nothing
    // dispatched); checked means dry_run: false (a real send, as before).
    // No longer gates whether this call happens at all — every Test Run now
    // produces a real trace in the Executions tab and in Temporal, which is
    // what was missing before (an unticked checkbox used to skip calling
    // /flows/run entirely, back when there was no dry-run option and running
    // it unconditionally risked an unwanted real send).
    if (compileResult) {
      const definitionWithCanvas = { ...compileResult.journey_definition, _canvas: flow };
      const journeyChannel = Array.from(activeChannels)[0];
      let journeyId = publishedJourney?.id;
      if (!journeyId) {
        const { journey, error: createError } = await createJourney({ name: botName, definition: definitionWithCanvas, ...(journeyChannel && { channel: journeyChannel }) });
        if (journey) {
          journeyId = journey.id;
          setPublishedJourney({ id: journey.id });
          // Only this branch (first-time creation) actually persists the
          // current canvas — a reused journeyId's own stored definition isn't
          // refreshed on every Test Run (the run itself always uses the fresh
          // `flow` from this call directly), so later edits made after that
          // point would still be lost on a reload; this stays dirty for those.
          setHasUnsavedChanges(false);
        } else {
          toast.error(createError || "Couldn't prepare this journey for a test run.");
        }
      }
      if (journeyId) {
        const { result: runResult, error: runError } = await runFlow(flow, journeyId, contactId, data.values, !data.deliver);
        // Advisory only — shown, never blocks the run.
        (runResult?.warnings || []).forEach((w) => toast.warning(typeof w === "string" ? w : w?.message || w?.reason || JSON.stringify(w)));
        if (runResult?.workflow_id) {
          const toastId = toast.loading("Test run started — waiting for the result…");
          const { detail, finished } = await waitForRunOutcome(runResult.workflow_id);
          const outcome = describeRunOutcome(detail, finished, runResult.workflow_id);
          toast[outcome.kind](outcome.title, { id: toastId, description: outcome.description });
        } else if (runResult) {
          toast.info("Test run started — check the Executions tab for the result.");
        } else if (runError) {
          toast.error(runError);
        }
      }
    } else {
      toast.error((compileError || "Couldn't compile this flow") + " — nothing to run.");
    }

    setCompletedNodeIds(new Set());
    setTriggerCompleted(true);
    setRunPaused(false);
    setRunSteps(steps);
    setRunIndex(0);
  };

  const handleSelectTrigger = () => {
    setSelectedNodeId(null);
    setInsertionTarget(null);
  };

  // Per direct ask: removing the trigger itself wipes the whole canvas back
  // to a truly blank slate — no trigger, no chain — not just the trigger's
  // own config, so the operator re-picks a trigger from the real catalog
  // (TriggerConfig's picker) exactly like a fresh from-scratch journey.
  const handleDeleteTrigger = () => {
    setTrigger(FALLBACK_TRIGGER);
    setChainRoot(null);
    setSelectedNodeId(null);
    setInsertionTarget(null);
    setPublishedJourney(null);
    invalidateTestRun();
  };

  // Picking a real trigger for the first time, from TriggerConfig's catalog
  // picker (click, or the drag below) — fires once; after this trigger.id is
  // never "" again for this session (changing trigger type later isn't a
  // supported flow, same as before this feature existed).
  const handlePickTrigger = (def: TriggerDef, category: JourneyCategory) => {
    if (def.status === "planned") return;
    setTrigger({ id: def.id, label: def.label, categoryId: category.id, config: {} });
  };

  // Hit-tests the one fixed trigger box on canvas (flow-canvas.tsx's
  // TRIGGER_DROP_ATTR), not a tree of "+" slots — a trigger can only ever go
  // in that one place, so this just checks whether the pointer is over it at
  // all rather than resolving *which* target like resolveDropTarget does.
  const isOverTriggerSlot = (x: number, y: number): boolean => {
    if (x < 0 || y < 0) return false;
    const el = document.elementFromPoint(x, y);
    return !!el?.closest(`[${TRIGGER_DROP_ATTR}]`);
  };

  const handleTriggerDragStart = (def: TriggerDef, category: JourneyCategory, x: number, y: number) => {
    if (def.status === "planned") return;
    setTriggerDragPayload({ trigger: def, category, x, y });
    setTriggerDragActive(isOverTriggerSlot(x, y));
  };

  const handleTriggerDragMove = (x: number, y: number) => {
    setTriggerDragPayload((prev) => (prev ? { ...prev, x, y } : prev));
    setTriggerDragActive(isOverTriggerSlot(x, y));
  };

  const handleTriggerDragEnd = (x: number, y: number) => {
    if (triggerDragPayload && isOverTriggerSlot(x, y)) handlePickTrigger(triggerDragPayload.trigger, triggerDragPayload.category);
    setTriggerDragPayload(null);
    setTriggerDragActive(false);
  };

  const handleSelectNode = (id: string) => {
    setSelectedNodeId(id);
    setInsertionTarget(null);
  };

  const handlePickInsertion = (target: InsertionTarget) => {
    setInsertionTarget(target);
    setSelectedNodeId(null);
  };

  const handleDeleteNode = (id: string) => {
    setChainRoot((prev) => pruneStaleAiChannels(deleteNode(prev, id)));
    if (selectedNodeId === id) setSelectedNodeId(null);
    invalidateTestRun();
  };

  // Shared by both ways to add a step: click a "+" slot then a palette item
  // (uses the currently-selected insertionTarget), or drag a palette item
  // straight onto a slot (the target comes from the drop itself, no
  // pre-selection needed).
  // A new AI Generate card pre-selects the bot's own default_channels
  // (GET /v1/bot/journeys) rather than the catalog's WhatsApp-only default.
  const API_TO_CHANNEL_LABEL: Record<string, string> = Object.fromEntries(Object.entries(CHANNEL_TO_API).map(([k, v]) => [v, k]));
  const journeyDefaultChannels = (journeyTemplates.find((t) => t.id === journeyId)?.defaultChannels || [])
    .map((c) => API_TO_CHANNEL_LABEL[c.toLowerCase()])
    .filter(Boolean);

  const insertActionAt = (target: InsertionTarget, actionId: string) => {
    const created = makeFlowNode(actionId);
    const newNode =
      created.type === "ai_generate" && journeyDefaultChannels.length
        ? { ...created, config: { ...created.config, channels: journeyDefaultChannels, channel: journeyDefaultChannels[0] } }
        : created;
    setChainRoot((prev) => insertNode(prev, target, newNode));
    setSelectedNodeId(newNode.id);
    setInsertionTarget(null);
    invalidateTestRun();
  };

  const handlePickPaletteItem = (action: ActionDef) => {
    if (!insertionTarget) {
      toast.error("Pick a \"+\" on the canvas first, then choose a step to add there — or drag it straight onto one.");
      return;
    }
    insertActionAt(insertionTarget, action.id);
  };

  // Resolves whichever "+" slot (if any) sits under a given viewport point,
  // by walking up from the actual element there to the nearest one carrying
  // the drop-target marker flow-canvas.tsx renders on every AddSlot.
  const resolveDropTarget = (x: number, y: number): InsertionTarget | null => {
    if (x < 0 || y < 0) return null;
    const el = document.elementFromPoint(x, y);
    const dropEl = el?.closest(`[${JOURNEY_DROP_ATTR}]`);
    const raw = dropEl?.getAttribute(JOURNEY_DROP_ATTR);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as InsertionTarget;
    } catch {
      return null;
    }
  };

  const handlePaletteDragStart = (action: ActionDef, x: number, y: number) => {
    setInsertionTarget(null);
    setDragPayload({ action, x, y });
    setDragHoverTarget(resolveDropTarget(x, y));
  };

  const handlePaletteDragMove = (x: number, y: number) => {
    setDragPayload((prev) => (prev ? { ...prev, x, y } : prev));
    setDragHoverTarget(resolveDropTarget(x, y));
  };

  const handlePaletteDragEnd = (x: number, y: number) => {
    const target = resolveDropTarget(x, y);
    if (target) insertActionAt(target, dragPayload!.action.id);
    setDragPayload(null);
    setDragHoverTarget(null);
  };

  const handleChangeTrigger = (patch: Record<string, any>) => {
    // `label` (set when the Trigger event dropdown changes — see
    // config-panel.tsx's TriggerBehaviorConfig) is the top-level
    // TriggerInstance field this panel's own header and the canvas trigger
    // card both display, not a config value — pulled out so it doesn't end
    // up stuck inside `config` where nothing reads it.
    const { label, ...configPatch } = patch;
    setTrigger((prev) => ({ ...prev, ...(label !== undefined ? { label } : {}), config: { ...prev.config, ...configPatch } }));
  };

  const handleChangeNode = (patch: Record<string, any>) => {
    if (!selectedNodeId) return;
    setInvalidNodeIds((prev) => {
      if (!prev.has(selectedNodeId)) return prev;
      const next = new Set(prev);
      next.delete(selectedNodeId);
      return next;
    });
    setChainRoot((prev) => {
      let next = prev;
      const rest = { ...patch };
      if ("trueBehavior" in rest) {
        next = setBranchBehavior(next, selectedNodeId, "true", rest.trueBehavior);
        delete rest.trueBehavior;
      }
      if ("falseBehavior" in rest) {
        next = setBranchBehavior(next, selectedNodeId, "false", rest.falseBehavior);
        delete rest.falseBehavior;
      }
      if ("thenBehavior" in rest) {
        next = setWaitThenBehavior(next, selectedNodeId, rest.thenBehavior);
        delete rest.thenBehavior;
      }
      if (Object.keys(rest).length) next = updateNodeConfig(next, selectedNodeId, rest);
      return next;
    });
    invalidateTestRun();
  };

  const handleChangeNodeType = (newType: string) => {
    if (!selectedNodeId) return;
    setChainRoot((prev) => changeSendNodeType(prev, selectedNodeId, newType));
    setInvalidNodeIds((prev) => {
      if (!prev.has(selectedNodeId)) return prev;
      const next = new Set(prev);
      next.delete(selectedNodeId);
      return next;
    });
    invalidateTestRun();
  };

  // Save draft — same POST /journeys / POST /journeys/{id}/versions pair
  // handlePublish uses, just without the final POST /journeys/{id}/publish
  // call, so this never goes live. Unlike handlePublish, not gated on a
  // passing compile — a draft can be incomplete; `_canvas` alone is enough
  // for chainFromSavedDefinition to reconstruct it later (it prefers
  // `_canvas` over the flat `.steps` fallback when present). Returns whether
  // it actually succeeded, so the leave-confirmation dialog below only
  // navigates away once the draft is genuinely saved, not just attempted.
  const handleSaveDraft = async (): Promise<boolean> => {
    setSavingDraft(true);
    try {
      const flow = serializeFlow(trigger, chainRoot);
      const definitionWithCanvas = { _canvas: flow };
      const journeyChannel = Array.from(activeChannels)[0];
      let journeyId = publishedJourney?.id;
      if (!journeyId) {
        const { journey, error } = await createJourney({ name: botName, definition: definitionWithCanvas, ...(journeyChannel && { channel: journeyChannel }) });
        if (!journey) {
          toast.error(error || "Couldn't save this draft.");
          return false;
        }
        journeyId = journey.id;
        setPublishedJourney({ id: journey.id });
      } else {
        const { version, error } = await createJourneyVersion(journeyId, definitionWithCanvas);
        if (!version) {
          toast.error(error || "Couldn't save a new draft version.");
          return false;
        }
      }
      setHasUnsavedChanges(false);
      toast.success("Draft saved.");
      return true;
    } finally {
      setSavingDraft(false);
    }
  };

  // Gate for the "<-"/"All Bots" back navigation — real browser navigation
  // (back/forward, tab close, refresh) is covered separately by the
  // beforeunload handler above, which can't show this app's own dialog (see
  // that handler's comment).
  const requestLeave = () => {
    if (hasUnsavedChanges) setShowLeaveConfirm(true);
    else navigate("/automate/bot-studio");
  };

  // Real publish — POST /journeys (first time) or POST /journeys/{id}/versions
  // (re-publishing after further edits), then POST /journeys/{id}/publish.
  // `compiledDefinition` is the step list from the last passing Test Run's
  // POST /v1/bot/flows/compile call, not the canvas's own graph — gated on
  // canPublish, so this only ever runs against something that both the old
  // test-run gate and the engine's own compile step agreed is runnable.
  const handlePublish = async () => {
    if (!canPublish || !compiledDefinition) return;
    setPublishing(true);
    try {
      // `definition` is a generic, backend-untyped object (confirmed live —
      // GET /journeys/{id}'s own schema has no fixed shape for it), so the
      // real executable step list from compile and this builder's own canvas
      // graph can both ride along in the same field without conflicting.
      // Re-opening this bot later (see chainFromSavedDefinition) prefers
      // `_canvas` when present — a byte-perfect reconstruction — over
      // best-effort reconstructing one from the flat step list alone.
      const definitionWithCanvas = { ...compiledDefinition, _canvas: serializeFlow(trigger, chainRoot) };

      // `channel` is a real, required-for-correctness field on POST /journeys
      // (confirmed live on JourneyCreateRequest) — omitting it was leaving
      // every bot's channel NULL server-side. Derived from whichever
      // Send<Channel> cards are actually in the chain, same source as the
      // Test Run gate's own primaryChannel above, so the two always agree.
      // `account` (the WABA channel_id) is deliberately NOT sent — this
      // builder has no account picker, and the classic bot-studio builder
      // (constant.ts) was already burned hardcoding one: it leaked a single
      // test WABA into every tenant's published journeys. Leaving it unset
      // lets the backend resolve the tenant's own WABA from the authenticated
      // request instead, same as that fix.
      const journeyChannel = Array.from(activeChannels)[0];

      let journeyId = publishedJourney?.id;
      let version: number | undefined;
      if (!journeyId) {
        const { journey, error } = await createJourney({ name: botName, definition: definitionWithCanvas, ...(journeyChannel && { channel: journeyChannel }) });
        if (!journey) {
          toast.error(error || "Couldn't save this journey.");
          return;
        }
        journeyId = journey.id;
        setPublishedJourney({ id: journey.id });
      } else {
        const { version: v, error } = await createJourneyVersion(journeyId, definitionWithCanvas);
        if (!v) {
          toast.error(error || "Couldn't save a new version.");
          return;
        }
        version = v.version;
      }
      const { ok, error: publishError } = await publish(journeyId, version);
      if (ok) {
        setHasUnsavedChanges(false);
        toast.success(`${botName} is live.`);
        navigate("/automate/bot-studio");
      } else {
        toast.error(publishError || "Couldn't publish this bot.");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleReset = () => {
    setChainRoot(makeDefaultTriggerChain());
    setSelectedNodeId(null);
    setInsertionTarget(null);
    invalidateTestRun();
    setPublishedJourney(null);
    setCenterCanvasTick((t) => t + 1);
    toast("Changes reset to the default trigger chain.");
  };

  if (catalogLoading || !seeded) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Loader2 size={22} className="animate-spin" />
          <p className="text-sm">{catalogLoadError ? "Couldn't load triggers and actions." : "Loading triggers and actions…"}</p>
          {/* A real reload rather than a soft retry — clears any stale
              module-level catalog cache (use-catalog.ts's loadedScopes),
              which a soft retry inside the same page load wouldn't touch. */}
          {catalogLoadError && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
            >
              Reload
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 shrink-0">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mb-1">
            <button type="button" onClick={requestLeave} className="p-0.5 hover:text-gray-600 dark:hover:text-gray-300">
              <ArrowLeft size={13} />
            </button>
            <button type="button" onClick={requestLeave} className="hover:underline">
              All Bots
            </button>
            <span>/</span>
            <span className="font-medium text-gray-700 dark:text-gray-200">{publishedJourney ? botName : "New Bot"}</span>
          </div>
          {editingName ? (
            <input
              autoFocus
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
              className="text-lg font-bold text-gray-900 dark:text-gray-100 bg-transparent border-b border-indigo-300 outline-none"
            />
          ) : (
            <button type="button" onClick={() => setEditingName(true)} className="flex items-center gap-1.5 text-lg font-bold text-gray-900 dark:text-gray-100">
              {botName}
              <Pencil size={14} className="text-gray-400" />
            </button>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Set triggers and actions for your bot</p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => toast("Analytics isn't available until this bot is published.")}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <BarChart3 size={14} /> View Analytics
          </button>
          <button type="button" onClick={handleReset} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
            <RotateCcw size={14} /> Reset Changes
          </button>
          <button
            type="button"
            disabled={savingDraft}
            onClick={handleSaveDraft}
            className="px-4 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/15 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-500/25 disabled:opacity-60 transition-colors"
          >
            {savingDraft ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            title={
              !compiledDefinition
                ? "Run a Test Run first — Publish unlocks once it passes."
                : (compileBlockers?.length ?? 0) > 0
                  ? compileBlockers![0]
                  : publishedJourney
                    ? "Saves a new version and republishes this bot."
                    : "Saves this bot as a real journey and publishes it."
            }
            disabled={!canPublish || publishing}
            onClick={() => {
              if (!compiledDefinition) {
                toast.error("Run a Test Run first — Publish unlocks once it passes.");
                return;
              }
              if ((compileBlockers?.length ?? 0) > 0) {
                toast.error(compileBlockers![0]);
                return;
              }
              handlePublish();
            }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-colors ${
              canPublish && !publishing ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-300 dark:bg-indigo-500/40 cursor-not-allowed"
            }`}
          >
            {publishing ? "Publishing…" : publishedJourney ? "Republish Bot" : "Publish Bot"}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        <AddPanel
          onPick={handlePickPaletteItem}
          disabled={!insertionTarget}
          onDragStart={handlePaletteDragStart}
          onDragMove={handlePaletteDragMove}
          onDragEnd={handlePaletteDragEnd}
        />

        <div ref={canvasWrapRef} className="relative flex-1 overflow-auto bg-gray-50 dark:bg-gray-950">
          <div style={{ transform: `scale(${canvasScale})`, transformOrigin: "top center" }}>
            <FlowCanvas
              trigger={trigger}
              chainRoot={chainRoot}
              selectedId={selectedNodeId}
              insertionTarget={insertionTarget}
              dragHoverTarget={dragHoverTarget}
              runningNodeId={runningNodeId}
              completedNodeIds={completedNodeIds}
              triggerCompleted={triggerCompleted}
              invalidNodeIds={invalidNodeIds}
              triggerDragActive={triggerDragActive}
              onSelectTrigger={handleSelectTrigger}
              onDeleteTrigger={handleDeleteTrigger}
              onSelectNode={handleSelectNode}
              onPickInsertion={handlePickInsertion}
              onDeleteNode={handleDeleteNode}
            />
          </div>

          {/* Zoom / fullscreen / Test Run — fixed to the viewport (not just
              the scroll container), so it's always visible without having to
              scroll down through however tall the canvas gets. Positioned
              between the two fixed-width side panels (AddPanel's 260px,
              ConfigPanel's 320px) so it stays centered over the canvas
              itself rather than the whole screen. */}
          <div className="fixed bottom-4 left-[260px] right-[320px] z-20 flex justify-center pointer-events-none">
            <div className="flex items-center gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-2 py-1.5 pointer-events-auto">
              <button type="button" onClick={() => setCanvasScale((s) => Math.min(1.5, +(s + 0.1).toFixed(2)))} title="Zoom in" className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100">
                <Plus size={16} />
              </button>
              <button type="button" onClick={() => setCanvasScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)))} title="Zoom out" className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100">
                <Minus size={16} />
              </button>
              <button type="button" onClick={toggleFullscreen} title="Fullscreen" className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100">
                <Expand size={16} />
              </button>
              <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
              {isRunning ? (
                <button
                  type="button"
                  onClick={() => setRunPaused((p) => !p)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                >
                  {runPaused ? <Play size={14} /> : <Pause size={14} />} {runPaused ? "Resume" : "Pause"}
                </button>
              ) : (
                <button type="button" onClick={handleTestRunClick} className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">
                  <Play size={14} /> Test Run
                </button>
              )}
              <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
              <button
                type="button"
                onClick={() => toast.info('Use the left panel, or a "+" on the canvas, to add a step.')}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400"
              >
                <PlusSquare size={16} /> Add
              </button>
            </div>
          </div>
        </div>

        <ConfigPanel
          trigger={trigger}
          selectedNode={selectedNode}
          awaitingInsertion={!!insertionTarget}
          precedingType={precedingType}
          precedingNode={precedingNode}
          journeyId={journeyId}
          savedJourneyId={publishedJourney?.id}
          onChangeTrigger={handleChangeTrigger}
          onChangeNode={handleChangeNode}
          onChangeNodeType={handleChangeNodeType}
          onPickTrigger={handlePickTrigger}
          onTriggerDragStart={handleTriggerDragStart}
          onTriggerDragMove={handleTriggerDragMove}
          onTriggerDragEnd={handleTriggerDragEnd}
        />
      </div>

      {testRunOpen && (
        <TestRunModal
          initial={testRunData}
          schema={testRunSchema}
          schemaLoading={testRunSchemaLoading}
          channels={activeChannels}
          onClose={() => setTestRunOpen(false)}
          onSubmit={handleTestRunSubmit}
        />
      )}

      {/* Per direct ask: a confirmation before leaving with unsaved work —
          covers the in-app "<-"/"All Bots" back navigation (requestLeave
          above); real browser navigation (back/forward, tab close, refresh)
          is covered separately by the beforeunload handler, which shows the
          browser's own dialog instead of this one. */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4" onClick={() => setShowLeaveConfirm(false)}>
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">Leave without saving?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">You have unsaved changes to this bot. Save your draft before leaving, or they'll be lost.</p>
            <div className="flex flex-col gap-2 mt-5">
              <button
                type="button"
                disabled={savingDraft}
                onClick={async () => {
                  const ok = await handleSaveDraft();
                  if (ok) {
                    setShowLeaveConfirm(false);
                    navigate("/automate/bot-studio");
                  }
                }}
                className="w-full py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors"
              >
                {savingDraft ? "Saving…" : "Save and leave"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  navigate("/automate/bot-studio");
                }}
                className="w-full py-2.5 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
              >
                Leave without saving
              </button>
              <button
                type="button"
                onClick={() => setShowLeaveConfirm(false)}
                className="w-full py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating ghost that follows the pointer while a palette item is
          being press-dragged (see add-panel.tsx) — purely visual feedback,
          the actual drop is resolved by hit-testing on release. */}
      {dragPayload && (
        <div
          className="fixed z-50 pointer-events-none px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold shadow-lg -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
          style={{ left: dragPayload.x, top: dragPayload.y }}
        >
          {dragPayload.action.paletteLabel}
        </div>
      )}
      {triggerDragPayload && (
        <div
          className="fixed z-50 pointer-events-none px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold shadow-lg -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
          style={{ left: triggerDragPayload.x, top: triggerDragPayload.y }}
        >
          {triggerDragPayload.trigger.label}
        </div>
      )}
    </div>
  );
};

export default CustomerJourneyBuilderPage;
